if (!customElements.get('video-hero-section')) {
  class VideoHeroSection extends HTMLElement {
    constructor() {
      super();
      this.cssAdded = false;
      this.youTubePlayers = {};
      this.init = this.init.bind(this);
    }

    connectedCallback() {
      document.addEventListener('shopify:section:load', this.onSectionLoad.bind(this));
      if (document.readyState !== 'loading') {
        this.init();
      } else {
        document.addEventListener('DOMContentLoaded', this.init);
      }
    }

    onSectionLoad(event) {
      if (event.target === this) {
        this.init();
      }
    }

    init() {
      if (!this.cssAdded) this.addCss();
      this.addProgressRings();
      this.observeVideos();
      this.setupPlayButtons();
    }

    addCss() {
      const style = document.createElement('style');
      style.textContent = `
        .video-hero__play-button-wrapper { position: relative; }
        .progress-ring {
          position: absolute; top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          width: 114%; height: 114%; z-index: 0;
        }
        .progress-ring__circle {
          transform: rotate(-90deg); transform-origin: 50% 50%;
          stroke: #DCAD2B; stroke-width: 3px; fill: transparent;
        }
        .video-hero__play-button { position: relative; z-index: 1; }
      `;
      document.head.appendChild(style);
      this.cssAdded = true;
    }

    addProgressRings() {
      this.querySelectorAll('.video-hero__play-button-wrapper').forEach(wrapper => {
        if (!wrapper.querySelector('.progress-ring')) {
          const svgNS = 'http://www.w3.org/2000/svg';
          const svg = document.createElementNS(svgNS, 'svg');
          svg.setAttribute('class', 'progress-ring');
          svg.setAttribute('viewBox', '0 0 120 120');
          const circle = document.createElementNS(svgNS, 'circle');
          circle.setAttribute('class', 'progress-ring__circle');
          circle.setAttribute('r', '56');
          circle.setAttribute('cx', '60');
          circle.setAttribute('cy', '60');
          const c = 2 * Math.PI * 56;
          circle.style.strokeDasharray = `${c} ${c}`;
          circle.style.strokeDashoffset = c;
          svg.appendChild(circle);
          wrapper.insertBefore(svg, wrapper.querySelector('.video-hero__play-button'));
        }
      });
    }

    observeVideos() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const el = entry.target;
          const id = el.dataset.sectionId;
          const type = el.dataset.type;
          const loaded = el.dataset.loaded;

          if (entry.isIntersecting) {
            if (!loaded) {
              this.loadVideo(el, id, type);
              el.dataset.loaded = 'true';
            } else if (el.dataset.playing === 'false') {
              this.play(el, type, id);
            }
          } else {
            if (el.dataset.playing === 'true') {
              this.pause(el, type, id);
            }
          }
        });
      }, { threshold: 0.01 });

      this.querySelectorAll('video-hero').forEach(el => observer.observe(el));
    }

    setupPlayButtons() {
      this.querySelectorAll('.video-hero__play-button').forEach(button => {
        const clone = button.cloneNode(true);
        button.parentNode.replaceChild(clone, button);
        clone.addEventListener('click', () => {
          const id = clone.dataset.sectionId;
          const el = this.querySelector(`video-hero[data-section-id="${id}"]`);
          const type = el?.dataset.type;
          const action = clone.dataset.action;
          if (action === 'pause') {
            this.pause(el, type, id);
          } else {
            this.play(el, type, id);
          }
        });
      });
    }

    loadVideo(el, id, type) {
      el.dataset.playing = 'true';
      const btn = this.querySelector(`.video-hero__play-button[data-section-id="${id}"]`);
      if (btn) btn.dataset.action = 'pause';

      if (type === 'mp4') {
        const video = el.querySelector('video');
        if (video) {
          video.muted = true;
          video.play().catch(console.warn);
          video.addEventListener('play', () => this.updateBtn(id, 'pause'));
          video.addEventListener('pause', () => this.updateBtn(id, 'play'));
          video.addEventListener('timeupdate', () => this.progress(id, video.currentTime / video.duration * 100));
        }
      } else if (type === 'youtube') {
        const vid = this.extractYouTubeID(el.dataset.videoId);
        if (!window.YT) {
          const s = document.createElement('script');
          s.src = "https://www.youtube.com/iframe_api";
          document.body.appendChild(s);
          window.onYouTubeIframeAPIReady = () => this.createYouTubePlayer(el, id, vid);
        } else {
          this.createYouTubePlayer(el, id, vid);
        }
      } else if (type === 'vimeo') {
        const vid = this.extractVimeoID(el.dataset.videoId);
        const iframe = document.createElement('iframe');
        iframe.src = `https://player.vimeo.com/video/${vid}?background=0&autoplay=1&muted=1&loop=1&transparent=1`;
        iframe.setAttribute('allow', 'autoplay; fullscreen');
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('width', '100%');
        iframe.setAttribute('height', '100%');
        iframe.className = 'vimeo-iframe';
        el.innerHTML = '';
        el.appendChild(iframe);
      }
    }

    createYouTubePlayer(el, id, vid) {
      const div = document.createElement('div');
      el.appendChild(div);
      this.youTubePlayers[id] = new YT.Player(div, {
        videoId: vid,
        playerVars: {
          autoplay: 1, mute: 1, rel: 0, loop: 1,
          controls: 0, modestbranding: 1, playsinline: 1, playlist: vid,
          origin: window.location.origin 
        },
        events: {
          onReady: e => e.target.playVideo(),
          onStateChange: e => {
            if (e.data === YT.PlayerState.PLAYING) this.trackYT(id);
          }
        }
      });
    }

    play(el, type, id) {
      el.dataset.playing = 'true';
      this.updateBtn(id, 'pause');
      if (type === 'youtube') this.youTubePlayers[id]?.playVideo();
      else if (type === 'vimeo') el.querySelector('iframe')?.contentWindow.postMessage({ method: 'play' }, '*');
      else el.querySelector('video')?.play();
    }

    pause(el, type, id) {
      el.dataset.playing = 'false';
      this.updateBtn(id, 'play');
      if (type === 'youtube') this.youTubePlayers[id]?.pauseVideo();
      else if (type === 'vimeo') el.querySelector('iframe')?.contentWindow.postMessage({ method: 'pause' }, '*');
      else el.querySelector('video')?.pause();
    }

    updateBtn(id, state) {
      const btn = this.querySelector(`.video-hero__play-button[data-section-id="${id}"]`);
      if (btn) btn.dataset.action = state;
    }

    progress(id, percent) {
      const wrapper = this.querySelector(`.video-hero__play-button[data-section-id="${id}"]`)?.closest('.video-hero__play-button-wrapper');
      const circle = wrapper?.querySelector('.progress-ring__circle');
      if (circle) {
        const r = +circle.getAttribute('r');
        const c = 2 * Math.PI * r;
        circle.style.strokeDashoffset = c - (percent / 100) * c;
      }
    }

    trackYT(id) {
      const player = this.youTubePlayers[id];
      const el = this.querySelector(`video-hero[data-section-id="${id}"]`);
      if (!player || !el) return;
      clearInterval(el._ytInterval);
      el._ytInterval = setInterval(() => {
        if (el.dataset.playing === 'true') {
          const t = player.getCurrentTime(), d = player.getDuration();
          if (d > 0) this.progress(id, t / d * 100);
        }
      }, 100);
    }

    extractYouTubeID(url) {
      const match = url?.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=))([\w-]{11})/);
      return match?.[1] || '';
    }

    extractVimeoID(url) {
      const match = url?.match(/(?:vimeo\.com\/(?:video\/)?)(\d+)/);
      return match?.[1] || '';
    }
  }

  customElements.define('video-hero-section', VideoHeroSection);
}
